import type {
    Category, CategoryCountResponse,
    DetailQuery,
    DetailResponse, MeResponse,
    SearchQuery,
    SearchResponse, SortOption,
    TermsQuery,
    TermValuesQuery,
    TermValuesResponse
} from "./interfaces";

import * as Sentry from "@sentry/svelte";
import {
    getTraceData
} from "@sentry/core";


export class NvbsSearchEngineApi {
    private static base_url: string;
    private static token: string;

    private static sorts: {
        [category: string]: SortOption[]
    } = {};

    public static init(base_url: string, token: string) {
        this.base_url = base_url;
        this.token = token;
    }

    public static async getSorts(category: string): Promise<SortOption[]> {
        // if already fetched, return the cached value
        if (this.sorts && this.sorts[category]) {
            return this.sorts[category];
        }
        const response = await fetch(this.base_url + `/api/viewer/category/${category}/sortable`, {
            method: 'GET',
            headers: this.requestHeaders()
        });
        if (!response.ok) {
            this.check_non_ok(response,'Failed to fetch sorts');
        }
        const data = await response.json();
        if(!this.sorts) {
            this.sorts = {};
        }
        const _sorts:SortOption[] = [];
        for (const [key, value] of Object.entries(data)) {
            _sorts.push({
                key: key as string,
                label: value as string
            });
        }
        this.sorts[category] = _sorts;
        return _sorts;
    }

    private static check_non_ok(response: Response, message: string) {
        if(response.status === 401) {
            // if the response is 401, it means the token is invalid or expired
            // so we need to reload the page to get a new token
            window.location.reload();
        } else {
            throw new Error(message);
        }
    }

    public static async getCategories(): Promise<Category[]> {
        const response = await fetch(this.base_url + '/api/viewer/categories', {
            method: 'GET',
            headers: this.requestHeaders()
        });
        if (!response.ok) {
            this.check_non_ok(response,'Failed to fetch categories');
        }
        return await response.json();
    }

    public static async getCategoryCounts(args : {
        query?:string;
        ocr?: boolean;
        exact_match?: boolean;
    } = {}): Promise<CategoryCountResponse> {
        const url = new URL(this.base_url + '/api/viewer/category-count');
        const searchParams = new URLSearchParams();
        if (args.query) {
            searchParams.append('query', this.parse_query(args.query, args.exact_match) );
        }

        if(args.ocr) {
            searchParams.append('enable_ocr', '1');
        }

        url.search = decodeURIComponent(searchParams.toString()); // decodeURIComponent is needed to handle special characters as we need brackets
        const response = await fetch(url, {
            method: 'GET',
            headers: this.requestHeaders()
        });
        if (!response.ok) {
            this.check_non_ok(response,'Failed to fetch category counts');
        }
        return await response.json();
    }

    public static async search(query: SearchQuery): Promise<SearchResponse> {
        const url = new URL(this.base_url + '/api/viewer/search');
        let searchParams = new URLSearchParams();
        if (query.query.length > 0) {
            searchParams.append('query', this.parse_query(query.query, query.exact_match) );
        }

        if (query.category) {
            searchParams.append('category', query.category);
        } else {
            throw new Error('Category is required');
        }

        if (query.terms) {
            let index = 0;
            for (const [term, values] of Object.entries(query.terms)) {
                if (!values) {
                    continue;
                }
                for (const value of values) {
                    searchParams.append(`term[${index}]`, `${term}=${value}`);
                    index++;
                }
            }
        }

        if (query.page) {
            searchParams.append('page', query.page.toString());
        }

        if (query.limit) {
            searchParams.append('limit', query.limit.toString());
        }

        searchParams = this.handleTimestamps(searchParams, query.date_one, query.date_two);

        if (query.sort) {
            searchParams.append('sort[0]', query.sort);
        }
        if (query.ocr)    {
            searchParams.append('enable_ocr', '1');
        }
        url.search = decodeURIComponent(searchParams.toString()); // decodeURIComponent is needed to handle special characters as we need brackets

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: this.requestHeaders()
        });

        if (!response.ok) {
            this.check_non_ok(response,'Failed to search');
        }

        return await response.json();
    }

    public static async getDetail(query: DetailQuery) {
        const url = new URL(this.base_url + `/api/viewer/detail/${query.category}/detail`);
        let searchParams = new URLSearchParams();
        if (query.query.length > 0) {
            searchParams.append('query', this.parse_query(query.query, query.exact_match));
        }

        if (query.category) {
            searchParams.append('category', query.category);
        }

        if (query.terms) {
            let index = 0;
            for (const [term, values] of Object.entries(query.terms)) {
                if (!values) {
                    continue;
                }
                for (const value of values) {
                    searchParams.append(`term[${index}]`, `${term}=${value}`);
                    index++;
                }
            }
        }

        if (query.page) {
            searchParams.append('page', query.page.toString());
        }

        if (query.limit) {
            searchParams.append('limit', query.limit.toString());
        }

        searchParams = this.handleTimestamps(searchParams, query.date_one, query.date_two);

        if (query.sort) {
            searchParams.append('sort[0]', query.sort);
        }
        if (query.ocr) {
            searchParams.append('enable_ocr', '1');
        }
        if (query.index) {
            searchParams.append('index', query.index.toString());
        }

        url.search = decodeURIComponent(searchParams.toString()); // decodeURIComponent is needed to handle special characters as we need brackets

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: this.requestHeaders()
        });

        if (!response.ok) {
            this.check_non_ok(response,'Failed to search');
        }
        const resp = await response.json();
        const rest = Object.keys(resp).reduce((acc: any, key: string) => {
            if (key !== 'thumbnail' && key !== 'bestand') {
                acc[key] = resp[key];
            }
            return acc;
        }, {});
        const output: DetailResponse = {
            thumbnail: resp.thumbnail,
            bestand: resp.bestand,
            data: rest
        }

        return output;
    }

    public static async getTerms(query: TermsQuery) {
        const url = new URL(this.base_url + '/api/viewer/terms');
        const searchParams = new URLSearchParams();
        if (query.category) {
            searchParams.set('category', query.category);
        }
        url.search = decodeURIComponent(searchParams.toString()); // decodeURIComponent is needed to handle special characters as we need brackets
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: this.requestHeaders()
        });
        if (!response.ok) {
            this.check_non_ok(response,'Failed to fetch terms');
        }

        const resp = await response.json();

        return resp as {
            [key: string]: string;
        };
    }

    public static async getTermValues(query: TermValuesQuery) {
        const url = new URL(this.base_url + '/api/viewer/term-values');
        let searchParams = new URLSearchParams();
        if (query.category){
            searchParams.set('category', query.category);
        }
        if (query.existing_terms) {
            let index = 0;
            for (const [term, values] of Object.entries(query.existing_terms)) {
                if (!values) {
                    continue;
                }
                for (const value of values) {
                    searchParams.append(`existing_term[${index}]`, `${term}=${value}`);
                    index++;
                }
            }
        }
        if (query.term) {
            searchParams.set('term', query.term);
        }
        if (query.query) {
            searchParams.set('query', this.parse_query(query.query, query.exact_match) );
        }

        searchParams = this.handleTimestamps(searchParams, query.date_one, query.date_two);

        if (query.enable_ocr) {
            searchParams.set('enable_ocr', '1');
        }
        url.search = decodeURIComponent(searchParams.toString()); // decodeURIComponent is needed to handle special characters as we need brackets
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: this.requestHeaders()
        });
        if (!response.ok) {
            this.check_non_ok(response,'Failed to fetch term values');
        }
        const resp = await response.json() as {
            [key: string]: number;
        };
        const output: TermValuesResponse = {
            term: query.term,
            label: query.label,
            values: []
        }
        for (const [key, value] of Object.entries(resp)) {
            output.values.push({
                name: key,
                count: value
            });
        }
        return output;
    }

    public static async getSignedInUser(): Promise<MeResponse> {
        const url = new URL(this.base_url + '/api/viewer/me');
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: this.requestHeaders()
        });


        let resp = await response.json();

        if (!response.ok) {
            this.check_non_ok(response,'Failed to fetch signed in user');

            resp = {
                id: 0,
                name: '',
                email: '',
                has_premium: false,
            }

            console.warn("NvbsSearchEngineApi.getSignedInUser: Failed to fetch signed in user, returning empty user object", resp);
        }

        return resp as MeResponse;
    }

    public static getFileUrl(category: string, id: number) {
        return this.base_url + `/api/viewer/detail/${category}/${id}/file?auth-token=${this.token}`;
    }

    private static requestHeaders() {
        const traceData = getTraceData();

        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-auth-token': this.token,
            'Authorization': 'Bearer ' + this.token,
            'sentry-trace' : traceData['sentry-trace'] || '',
            'baggage': traceData['baggage'] || ''
        };
    }

    private static timestampMsToS(timestamp: number): string {
        const string = timestamp.toString();
        const length = string.length;
        // if longer than 3 remove the last 3 digits
        if (length > 3) {
            return string.substring(0, length - 3);
        }
        return string;
    }

    private static unixStartOfDay(number: number): number {
        const dayInMilliseconds = 24 * 60 * 60 * 1000;
        return number - dayInMilliseconds
    }

    private static unixEndOfDay(number: number): number {
        const dayInMilliseconds = 24 * 60 * 60 * 1000;
        return number + dayInMilliseconds
    }

    private static handleTimestamps(searchParams: URLSearchParams, date_one: number | null | undefined = 0, date_two: number | null | undefined = 0): URLSearchParams {

        if(date_one === 0 || date_two === 0) {
            return searchParams;
        }

        if(!date_one || !date_two) {
            return searchParams;
        }

        if(date_one === date_two) {
            date_one = this.unixStartOfDay(date_one);
            date_two = this.unixEndOfDay(date_two);
            console.info(date_one, date_two)
            searchParams.set('date_one', NvbsSearchEngineApi.timestampMsToS(date_one).toString());
            searchParams.set('date_two', NvbsSearchEngineApi.timestampMsToS(date_two).toString());

            return searchParams;
        }

        searchParams.set('date_one', NvbsSearchEngineApi.timestampMsToS(date_one).toString());
        searchParams.set('date_two', NvbsSearchEngineApi.timestampMsToS(date_two).toString());

        return searchParams;
    }



    private static parse_query(query: string, exact_match: boolean = false): string {
        let val = query;
        if (exact_match) {
            val = val.split(" ").map(v => `"${v}"`).join(" ");
        }
        return val;
    }
}
